/**
 * Referral Payouts API Client
 *
 * Handles API calls for mint referral payout management.
 */

import { apiClient } from './client';

export interface ReferralBalance {
  total_accrued_drops: number;
  total_paid_drops: number;
  inflight_drops: number;
  available_balance_drops: number;
  total_balance_drops: number;
  total_referred_mints: number;
}

export interface ReferralLaunchEarnings {
  referrer_user_id: string;
  launch_id: string;
  launch_name: string;
  launch_slug?: string;
  current_referral_bps: number | null;
  referred_mints: number;
  total_earned_drops: number;
}

export interface ReferralPayout {
  id: string;
  user_id: string;
  payout_address: string;
  amount_drops: number;
  status: string;
  approved_by: string | null;
  approved_at: string | null;
  rejected_reason: string | null;
  tx_hash: string | null;
  ledger_index: number | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface ReferralBalanceResponse {
  success: boolean;
  balance: ReferralBalance;
  earnings: ReferralLaunchEarnings[];
  recent_payouts: ReferralPayout[];
  has_active_payout: boolean;
}

/**
 * Get the current user's referral balance, earnings, and payout history
 */
export async function getReferralBalance(): Promise<ReferralBalanceResponse> {
  const response = await apiClient.get<ReferralBalanceResponse>(
    '/referral-payouts/balance'
  );

  if (!response.success) {
    throw new Error('Failed to get referral balance');
  }

  return response;
}

/**
 * Request a payout of available balance
 */
export async function requestReferralPayout(): Promise<{ success: boolean; payout: ReferralPayout }> {
  const response = await apiClient.post<{ success: boolean; payout: ReferralPayout; error?: string }>(
    '/referral-payouts/request'
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to request payout');
  }

  return response;
}
